// src/pages/orders/components/bulk/EditOrderModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { X, Check, Trash2, Plus } from 'lucide-react';
import useCitySearchForBulkOrder from '../../../../hooks/useCitySearchForBulkOrder';
import useProductSearchForBulkOrder from '../../../../hooks/useProductSearchForBulkOrder';
import { getCities } from '../../../../services/api/orderReference';
import { formatPhoneNumbers } from '../../../../utils/phoneNumberFormatter';
import { useAuth } from '../../../../hooks/useAuth';
import styles from './EditOrderModal.module.css';

const EditOrderModal = ({ row, validationResult, onSave, onClose, shopId, isResellerDropshipper = false }) => {
  const [editedData, setEditedData] = useState({
    customerName: row.customerName || '',
    customerEmail: row.customerEmail || '',
    address: row.address || '',
    phoneNumbers: [...(row.phoneNumbers?.length > 0 ? row.phoneNumbers : (row.rawPhones || []))],
    cityName: row.cityName || '',
    paymentType: row.paymentType || 'cash_on_delivery',
    orderStatus: row.orderStatus || 'pending',
    privateReference: row.privateReference || '',
    deliveryNotes: row.deliveryNotes || '',
    specialNotes: row.specialNotes || '',
    items: [...(row.items || [])],
    deliveryCharge: row.deliveryCharge || '',
    totalDiscount: row.totalDiscount || ''
  });

  const [cities, setCities] = useState([]);
  const [editingField, setEditingField] = useState(null);
  const cityInputRef = useRef(null);
  const productInputRefs = useRef({});
  const { user } = useAuth();

  // Load cities on mount
  useEffect(() => {
    getCities({includeLocation : true}).then(response => {
      const citiesData = response?.cities || response || [];
      setCities(Array.isArray(citiesData) ? citiesData : []);
    });
  }, []);

  // City search hook
  const citySearch = useCitySearchForBulkOrder(cities, (city) => {
    setEditedData(prev => ({
      ...prev,
      cityName: city.name
    }));
    citySearch.closeDropdown();
    setEditingField(null);
  });

  // Product search hook
  const productSearch = useProductSearchForBulkOrder(shopId, (product) => {
    if (editingField && editingField.startsWith('product-')) {
      const itemIndex = parseInt(editingField.split('-')[1]);
      setEditedData(prev => {
        const updatedItems = [...prev.items];
        updatedItems[itemIndex] = {
          ...updatedItems[itemIndex],
          sku: product.sku,
          productName: product.displayName,
          quantity: updatedItems[itemIndex]?.quantity || 1,
          customLineTotal: updatedItems[itemIndex]?.customLineTotal || null
        };
        return {
          ...prev,
          items: updatedItems
        };
      });
      productSearch.closeDropdown();
      setEditingField(null);
    }
  });

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhoneChange = (index, value) => {
    setEditedData(prev => {
      const updatedPhones = [...prev.phoneNumbers];
      updatedPhones[index] = value;
      return {
        ...prev,
        phoneNumbers: updatedPhones
      };
    });
  };

  const addPhoneNumber = () => {
    setEditedData(prev => ({
      ...prev,
      phoneNumbers: [...prev.phoneNumbers, '']
    }));
  };

  const removePhoneNumber = (index) => {
    setEditedData(prev => ({
      ...prev,
      phoneNumbers: prev.phoneNumbers.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (index, field, value) => {
    setEditedData(prev => {
      const updatedItems = [...prev.items];
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value
      };
      return {
        ...prev,
        items: updatedItems
      };
    });
  };

  const addItem = () => {
    setEditedData(prev => ({
      ...prev,
      items: [...prev.items, { sku: '', productName: '', quantity: 1, customLineTotal: null }]
    }));
  };

  const removeItem = (index) => {
    setEditedData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleCityFieldFocus = () => {
    // Seed the search term with the current city value so it doesn't disappear
    citySearch.handleSearchChange(editedData.cityName || '');
    setEditingField('cityName');
    citySearch.openDropdown();
  };

  const handleCityInputChange = (e) => {
    const value = e.target.value;
    // Always update search term when editing field is active
    citySearch.handleSearchChange(value);
    // Also keep editedData in sync while typing so if user blurs without
    // selecting from dropdown the typed value is preserved
    setEditedData(prev => ({ ...prev, cityName: value }));
  };

  const handleCityBlur = () => {
    // Small delay so click on dropdown item fires before blur closes it
    setTimeout(() => {
      if (editingField === 'cityName') {
        citySearch.closeDropdown();
        setEditingField(null);
      }
    }, 150);
  };

  const handleProductFieldClick = (index) => {
    setEditingField(`product-${index}`);
    productSearch.openDropdown();
  };

  const handleSave = () => {
    // Format phone numbers before saving
    const businessCountryCode = user?.business?.countryCode || 'LK';
    const rawPhones = editedData.phoneNumbers.filter(p => p && p.trim());
    
    if (rawPhones.length > 0) {
      const phoneResult = formatPhoneNumbers(rawPhones, businessCountryCode);
      
      if (!phoneResult.isValid) {
        // Show phone errors
        alert('Phone validation errors:\n' + phoneResult.errors.join('\n'));
        return;
      }
      
      // Save with formatted phone numbers
      onSave({
        ...editedData,
        phoneNumbers: phoneResult.formattedNumbers
      });
    } else {
      onSave(editedData);
    }
  };

  const getFieldErrors = (field) => {
    if (!validationResult?.errors) return [];
    return validationResult.errors.filter(error => 
      error.toLowerCase().includes(field.toLowerCase())
    );
  };

  const hasFieldError = (field) => {
    return getFieldErrors(field).length > 0;
  };

  // The city input always shows the searchTerm when in editing mode,
  // otherwise it shows the saved cityName value.
  const cityInputValue = editingField === 'cityName'
    ? citySearch.searchTerm
    : editedData.cityName;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Edit Order - Row {row.rowNumber}</h3>
          <button onClick={onClose} className={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Customer Information */}
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Customer Information</h4>
            
            <div className={styles.formGroup}>
              <label className={hasFieldError('customer name') ? styles.errorLabel : ''}>
                Customer Name <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={editedData.customerName}
                onChange={(e) => handleFieldChange('customerName', e.target.value)}
                className={hasFieldError('customer name') ? styles.errorInput : ''}
              />
              {hasFieldError('customer name') && (
                <div className={styles.fieldErrors}>
                  {getFieldErrors('customer name').map((error, i) => (
                    <span key={i}>{error}</span>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label className={hasFieldError('email') ? styles.errorLabel : ''}>
                Customer Email
              </label>
              <input
                type="email"
                value={editedData.customerEmail}
                onChange={(e) => handleFieldChange('customerEmail', e.target.value)}
                className={hasFieldError('email') ? styles.errorInput : ''}
              />
              {hasFieldError('email') && (
                <div className={styles.fieldErrors}>
                  {getFieldErrors('email').map((error, i) => (
                    <span key={i}>{error}</span>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label className={hasFieldError('phone') ? styles.errorLabel : ''}>
                Phone Numbers <span className={styles.required}>*</span>
              </label>
              {editedData.phoneNumbers.map((phone, index) => (
                <div key={index} className={styles.phoneRow}>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => handlePhoneChange(index, e.target.value)}
                    placeholder={`Phone ${index + 1}`}
                    className={hasFieldError('phone') ? styles.errorInput : ''}
                  />
                  {editedData.phoneNumbers.length > 1 && (
                    <button
                      onClick={() => removePhoneNumber(index)}
                      className={styles.removeButton}
                      type="button"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button onClick={addPhoneNumber} className={styles.addButton} type="button">
                <Plus size={16} /> Add Phone Number
              </button>
              {hasFieldError('phone') && (
                <div className={styles.fieldErrors}>
                  {getFieldErrors('phone').map((error, i) => (
                    <span key={i}>{error}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Delivery Information */}
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Delivery Information</h4>
            
            <div className={styles.formGroup}>
              <label className={hasFieldError('address') ? styles.errorLabel : ''}>
                Address <span className={styles.required}>*</span>
              </label>
              <textarea
                value={editedData.address}
                onChange={(e) => handleFieldChange('address', e.target.value)}
                rows={3}
                className={hasFieldError('address') ? styles.errorInput : ''}
              />
              {hasFieldError('address') && (
                <div className={styles.fieldErrors}>
                  {getFieldErrors('address').map((error, i) => (
                    <span key={i}>{error}</span>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label className={hasFieldError('city') ? styles.errorLabel : ''}>
                City <span className={styles.required}>*</span>
              </label>
              <div className={styles.searchContainer}>
                <input
                  ref={cityInputRef}
                  type="text"
                  value={cityInputValue}
                  onChange={handleCityInputChange}
                  onFocus={handleCityFieldFocus}
                  onBlur={handleCityBlur}
                  onKeyDown={citySearch.handleKeyDown}
                  placeholder="Search city..."
                  className={hasFieldError('city') ? styles.errorInput : ''}
                />
                {citySearch.showResults && editingField === 'cityName' && (
                  <div className={styles.searchDropdown}>
                    {citySearch.filteredCities.length > 0 ? (
                      citySearch.filteredCities.map((city, index) => (
                        <div
                          key={city.id}
                          className={`${styles.searchItem} ${
                            index === citySearch.selectedIndex ? styles.selected : ''
                          }`}
                          // Use onMouseDown instead of onClick so it fires before onBlur
                          onMouseDown={(e) => {
                            e.preventDefault(); // prevent input blur before selection
                            citySearch.selectCity(city);
                            setEditingField(null);
                          }}
                        >
                          {citySearch.formatCityDisplay(city)}
                        </div>
                      ))
                    ) : (
                      <div className={styles.noResults}>No cities found</div>
                    )}
                  </div>
                )}
              </div>
              {hasFieldError('city') && (
                <div className={styles.fieldErrors}>
                  {getFieldErrors('city').map((error, i) => (
                    <span key={i}>{error}</span>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label>Delivery Notes</label>
              <textarea
                value={editedData.deliveryNotes}
                onChange={(e) => handleFieldChange('deliveryNotes', e.target.value)}
                rows={2}
              />
            </div>
          </div>

          {/* Order Information */}
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Order Information</h4>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Payment Type</label>
                <select
                  value={editedData.paymentType}
                  onChange={(e) => handleFieldChange('paymentType', e.target.value)}
                >
                  <option value="">Select Payment Type</option>
                  <option value="cash_on_delivery">Cash on Delivery</option>
                  <option value="fully_paid">Fully Paid</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Order Status</label>
                <select
                  value={editedData.orderStatus}
                  onChange={(e) => handleFieldChange('orderStatus', e.target.value)}
                >
                  <option value="">Select Status</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                </select>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Private Reference</label>
              <input
                type="text"
                value={editedData.privateReference}
                onChange={(e) => handleFieldChange('privateReference', e.target.value)}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Special Notes</label>
              <textarea
                value={editedData.specialNotes}
                onChange={(e) => handleFieldChange('specialNotes', e.target.value)}
                rows={2}
              />
            </div>
          </div>

          {/* Items */}
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Order Items <span className={styles.required}>*</span></h4>
            
            {editedData.items.map((item, index) => (
              <div key={index} className={styles.itemCard}>
                <div className={styles.itemHeader}>
                  <span>Item {index + 1}</span>
                  {editedData.items.length > 1 && (
                    <button
                      onClick={() => removeItem(index)}
                      className={styles.removeButton}
                      type="button"
                    >
                      <Trash2 size={16} /> Remove
                    </button>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={hasFieldError('sku') || hasFieldError('item') ? styles.errorLabel : ''}>
                    Product <span className={styles.required}>*</span>
                  </label>
                  <div className={styles.searchContainer}>
                    <input
                      ref={(el) => productInputRefs.current[index] = el}
                      type="text"
                      value={
                        editingField === `product-${index}` 
                          ? productSearch.searchTerm 
                          : item.productName || item.sku
                      }
                      onChange={(e) => {
                        if (editingField === `product-${index}`) {
                          productSearch.handleSearchChange(e.target.value);
                        } else {
                          handleItemChange(index, 'sku', e.target.value);
                        }
                      }}
                      onFocus={() => handleProductFieldClick(index)}
                      onKeyDown={productSearch.handleKeyDown}
                      placeholder="Search product by name or SKU..."
                      className={hasFieldError('sku') || hasFieldError('item') ? styles.errorInput : ''}
                    />
                    {productSearch.showResults && editingField === `product-${index}` && (
                      <div className={styles.searchDropdown}>
                        {productSearch.isSearching ? (
                          <div className={styles.searching}>Searching...</div>
                        ) : productSearch.searchResults.length > 0 ? (
                          productSearch.searchResults.map((product, idx) => (
                            <div
                              key={`${product.id}-${product.variationId || 'simple'}`}
                              className={`${styles.searchItem} ${
                                idx === productSearch.selectedIndex ? styles.selected : ''
                              } ${product.isOutOfStock ? styles.outOfStock : ''}`}
                              onClick={() => !product.isOutOfStock && productSearch.selectProduct(product)}
                            >
                              <div className={styles.productInfo}>
                                <span className={styles.productName}>{product.formattedName}</span>
                                <span className={styles.productSku}>SKU: {product.sku}</span>
                                <span className={styles.productPrice}>{product.formattedPrice}</span>
                                {!product.stockHidden && (
                                  <span className={styles.productStock}>{product.formattedStock}</span>
                                )}
                              </div>
                            </div>
                          ))
                        ) : productSearch.hasSearched ? (
                          <div className={styles.noResults}>No products found</div>
                        ) : null}
                      </div>
                    )}
                  </div>
                  {(hasFieldError('sku') || hasFieldError('item')) && (
                    <div className={styles.fieldErrors}>
                      {[...getFieldErrors('sku'), ...getFieldErrors('item')].map((error, i) => (
                        <span key={i}>{error}</span>
                      ))}
                    </div>
                  )}
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={hasFieldError('quantity') ? styles.errorLabel : ''}>
                      Quantity <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                      min="1"
                      className={hasFieldError('quantity') ? styles.errorInput : ''}
                    />
                    {hasFieldError('quantity') && (
                      <div className={styles.fieldErrors}>
                        {getFieldErrors('quantity').map((error, i) => (
                          <span key={i}>{error}</span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label>Custom Line Total (Optional)</label>
                    <input
                      type="number"
                      value={item.customLineTotal || ''}
                      onChange={(e) => handleItemChange(index, 'customLineTotal', e.target.value ? parseFloat(e.target.value) : null)}
                      step="0.01"
                      placeholder="Leave empty for auto-calculation"
                    />
                  </div>
                </div>
              </div>
            ))}

            <button onClick={addItem} className={styles.addButton} type="button">
              <Plus size={16} /> Add Item
            </button>
          </div>

          {/* Financial Information */}
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Financial Information</h4>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Delivery Charge</label>
                <input
                  type="number"
                  value={editedData.deliveryCharge}
                  onChange={(e) => handleFieldChange('deliveryCharge', e.target.value ? parseFloat(e.target.value) : '')}
                  step="0.01"
                />
              </div>

              <div className={styles.formGroup}>
                <label>Total Discount</label>
                <input
                  type="number"
                  value={editedData.totalDiscount}
                  onChange={(e) => handleFieldChange('totalDiscount', e.target.value ? parseFloat(e.target.value) : '')}
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* All Validation Errors */}
          {validationResult?.errors && validationResult.errors.length > 0 && (
            <div className={styles.allErrors}>
              <h4>All Validation Errors:</h4>
              {validationResult.errors.map((error, i) => (
                <div key={i} className={styles.errorItem}>{error}</div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.cancelButton} type="button">
            Cancel
          </button>
          <button onClick={handleSave} className={styles.saveButton} type="button">
            <Check size={18} /> Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditOrderModal;